/*eslint-disable*/
import React, { useEffect, useState, useContext } from "react";
import { useImmer } from "use-immer";
import cx from "classnames";

// External libraries
import { ThreeDots } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";
import { toast, Slide as ToastSlide } from "react-toastify";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Chip,
  Drawer,
  Tooltip,
} from "@material-ui/core";

// material-ui icons
import {
  AddCircle,
  Chat,
  Delete,
  Edit,
  RateReview,
} from "@material-ui/icons";

// Internal components and utilities
import AuthContext from "../../contexts/AuthProvider";
import { canDo } from "../../utils/routeAccess";
import { client } from "../../services/axiosClient";
import Button from "../../components/CustomButtons/Button";
import Card from "../../components/Card/Card.js";
import CardBody from "../../components/Card/CardBody.js";
import CardIcon from "../../components/Card/CardIcon.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CustomInput from "../../components/CustomInput/CustomInput";
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import SnackbarContent from "../../components/Snackbar/SnackbarContent";
import CustomReactSelect from "../../components/CustomDropdown/CustomReactSelect";
import DeleteCommentDialog from "../../components/Dialog/DeleteCommentDialog";
import DeleteAdjustmentDialog from "../../components/Dialog/DeleteAdjustmentDialog";
import CommentsDialog from "../../components/Dialog/CommentsDialog";
import TextEditor from "../../components/TextEditor/TextEditor";
import ModalHeader from "../../components/ModalHeader";

import {
  cardTitle,
  grayColor,
  whiteColor,
  dangerColor,
  warningColor,
  blackColor,
  defaultFont,
} from "../../assets/jss/material-dashboard-pro-react";

// hooks
import useThemeColors from "../../hooks/useThemeColors";

const groupsStyle = (theme) => ({
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
  },
  cardBody: {
    padding: "0.9375rem 20px",
    flex: "1 1 auto",
    WebkitBoxFlex: "1",
    position: "relative",
  },
  comments: {
    width: "100%",
    marginTop: "12px",
    marginBottom: "12px",
    fontSize: "12px",
    lineHeight: "1.4",
  },
  snackbarPaper: {
    width: "100%",
    marginBottom: "8px",
  },
  snackbarMessage: {
    maxWidth: "100% !important",
  },
  addNewAdjustmentButton: {
    marginTop: "4px",
    "& > button": {
      paddingLeft: "8px",
      paddingRight: "15px",
      marginRight: "5px",
      "& > span > svg": {
        marginTop: "-2px",
      },
    },
  },
  adjustmentsTable: {
    marginTop: "8px",
    width: "100%",
    fontWeight: "300",
    borderCollapse: "separate",
    borderSpacing: "0 8px",
},
  adjustmentsTableHead: {
    fontWeight: "400",
  },
  adjustmentsTableBody: {
    color: grayColor[6],
    "&.saving": {
      opacity: "0.75",
      pointerEvents: "none",
    },
  },
  adjustmentsTableRow: {
    transition: "all 0.15s ease",
    cursor: "pointer",
    "&:nth-child(odd)": {
      backgroundColor: blackColor + "08",
    },
    "&:hover": {
      // backgroundColor: blackColor + "11",
      backgroundColor: theme.palette.custom.clientColor + "11",
    },
  },
  adjustmentsTableColDescription: {
    // width: "17%",
  },
  adjustmentsTableColPriority: {
    // width: "5%",
    textAlign: "center",
  },
  priorityChip: {
    fontSize: "0.75rem",
    height: "20px",
    fontWeight: 500,
  },
  priorityChipHigh: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    "&:hover": {
      backgroundColor: "#ffcdd2",
    },
  },
  priorityChipMedium: {
    backgroundColor: "#fff8e1",
    color: "#f57f17",
    "&:hover": {
      backgroundColor: "#ffecb3",
    },
  },
  priorityChipLow: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    "&:hover": {
      backgroundColor: "#c8e6c9",
    },
  },
  priorityChipCompleted: {
    backgroundColor: grayColor[14],
    color: grayColor[8],
    "&:hover": {
      backgroundColor: grayColor[13],
    },
  },
  adjustmentsTableRowCompleted: {
    opacity: 0.5,
    color: grayColor[6],
  },
  adjustmentsTableColPnlLines: {
    // width: "20%",
    textAlign: "center",
  },
  adjustmentsTableColValidators: {
    // width: "20%",
    textAlign: "center",
  },
  adjustmentsTableColStage: {
    // width: "15%",
    textAlign: "center",
  },
  adjustmentsTableColPlannedDate: {
    // width: "10%",
    textAlign: "center",
  },
  adjustmentsTableColNextStep: {
    // width: "15%",
    textAlign: "center",
  },
  adjustmentsTableColActions: {
    // width: "8%",
    textAlign: "center",
  },
  iconButtonCustom: {
    cursor: "pointer",
    marginLeft: "3px",
    marginRight: "3px",
    marginTop: "2px",
    transition: "all 0.2s ease-in-out",
    fontSize: "20px !important",
    color: grayColor[6],
    "&:hover": {
      color: theme.palette.custom.clientColor,
    },
    "&.disabled": {
      color: grayColor[11],
      cursor: "not-allowed",
      pointerEvents: "none",
      opacity: 0.5,
      "&:hover": {
        color: grayColor[11],
      },
    },
    "&.saveButton": {
      height: "30px",
      width: "30px",
      "&:hover": {
        color: theme.palette.custom.clientColor,
      },
      "&.savePending:not(.savingGroup)": {
        color: warningColor[7],
        animation: "$attention 3s ease infinite",
      },
      "&.savingGroup": {
        color: warningColor[7],
        opacity: "0.75",
        animation: "$saving 1s ease-in-out infinite",
        pointerEvents: "none",
      },
      "&:not(.savePending)": {
        color: grayColor[4],
        pointerEvents: "none",
      },
    },
    "&.removeButton": {
      height: "35px",
      width: "35px",
      marginBottom: "-2px",
      "&.saving": {
        color: grayColor[4],
      },
      "&:not(.saving)": {
        "&:hover": {
          color: dangerColor[0],
        },
      },
    },
  },
  selectFormControl: {
    paddingTop: "7px",
    margin: "7px 0 7px 0 !important",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: theme.palette.custom.clientColor + "!important",
      },
    },
  },
  "@keyframes attention": {
    "10%": {
      transform: "scale(1.2)",
    },
    "13.33%,20%": {
      transform: "rotate(-10deg) scale(1.2)",
    },
    "16.67%": {
      transform: "rotate(10deg) scale(1.2)",
    },
    "23.33%": {
      transform: "rotate(0deg) scale(1.2)",
    },
    "33%": {
      transform: "scale(1)",
    },
  },
  "@keyframes saving": {
    "0%": {
      opacity: "0.75",
    },
    "50%": {
      opacity: "0.25",
    },
    "100%": {
      opacity: "0.75",
    },
  },
  stageIconWrapper: {
    height: "10px",
    width: "10px",
    borderRadius: "3px",
    position: "relative",
    display: "inline-block",
    margin: "4px 1px 0",
    "&:not(.done),&:not(.current),": {
      color: grayColor[3],
      backgroundColor: grayColor[10],
    },
    "&.done": {
      color: theme.palette.custom.clientColor,
      backgroundColor: theme.palette.custom.clientColor + "88",
    },
    "&.current": {
      color: theme.palette.custom.clientTextColor || whiteColor,
      backgroundColor: theme.palette.custom.clientColor,
    },
  },
  stageIcon: {
    height: "20px",
    width: "20px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
  },
  drawerHeader: {
    borderBottom: "1px solid " + theme.palette.custom.clientColor,
    padding: "4px 12px",
    backgroundColor: grayColor[15],
  },
  drawerHeaderScope: {
    fontSize: "12px",
    color: grayColor[6],
  },
  drawerButton: {
    // width: "120px",
    borderRadius: "5px !important"
  },
  labelRoot: {
    ...defaultFont,
    color: grayColor[3] + " !important",
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "1.42857",
    marginTop: "10px",
    letterSpacing: "unset",
    transform: "translate(0, 1.5px) scale(0.75)",
    transformOrigin: "top left",
  },
});

export default function Adjustments() {
  /* region initial declarations */
  let { user, userDetails } = useContext(AuthContext);
  const { clientColor, clientTextColor } = useThemeColors();
  const useStyles = makeStyles(groupsStyle);
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  
  // Role-based access control
  const userRole = userDetails?.user_role?.toUpperCase().trim() || '';
  const isDev = userRole === 'DEV';
  const isCAD = userRole === 'CAD';
  // Acesso a pagina: atomo no sistema novo, role (DEV/CAD) no legado. As
  // regras de estagio abaixo continuam por role (isDev/isCAD).
  const hasAccess = canDo(userDetails, 'admin.adjustments.manage', ['DEV', 'CAD']);

  // Check access permission early
  if (!hasAccess) {
    return (
      <div>
        <GridContainer style={{marginTop: "-40px"}}>
          <GridItem xs={12}>
            <SnackbarContent
              message={t("adjustments.errors.access_denied")}
              color="danger"
            />
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  const notify = (message, type = "info", autoClose = 3000) =>
    toast(message, {
      position: "top-right",
      autoClose: autoClose,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "light",
      type: type,
      transition: ToastSlide,
    });
  const loadingSpinner = () => {
    return (
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <ThreeDots
          width="100px"
          height="100px"
          color={clientColor}
          ariaLabel="loading"
          wrapperStyle={{}}
          wrapperClass=""
        />
      </div>
    );
  };
  /* endregion */

  /* region state declarations */
  const [dataLoaded, setDataLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const [usersData, setUsersData] = useState([]);
  const [pnlStructure, setPnlStructure] = useState([]);
  const [pnlData, setPnlData] = useState({});
  const [adjustmentsData, setAdjustmentsData] = useImmer([]);
  const [selectedAdjustment, setSelectedAdjustment] = useImmer({});
  const [deleteCommentDialogOpen, setDeleteCommentDialogOpen] = useState(false);
  const [commentIdToDelete, setCommentIdToDelete] = useState(null);
  const [deletingComment, setDeletingComment] = useState(false);
  const [deleteAdjustmentDialogOpen, setDeleteAdjustmentDialogOpen] = useState(false);
  const [adjustmentIdToDelete, setAdjustmentIdToDelete] = useState(null);
  const [deletingAdjustment, setDeletingAdjustment] = useState(false);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [commentAdjustment, setCommentAdjustment] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [longDescriptionContent, setLongDescriptionContent] = useState('');
  const [plannedMonthInput, setPlannedMonthInput] = useState('');
  const [rejectionComment, setRejectionComment] = useState('');
  const [stageTransitionAction, setStageTransitionAction] = useState(null); // 'advance', 'reject', etc.

  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  /* endregion */

  /* region useEffect's */
  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);
  useEffect(() => {
    client
      .post("api/admin_adjustments/")
      .then((response) => {
        setUsersData(response.data.users);
        setPnlStructure(response.data.pnl_structure);
        
        // Create pnlData mapping for easy lookup
        const pnlDataMap = {};
        response.data.pnl_structure.forEach((line) => {
          pnlDataMap[line.meta_name] = line;
        });
        setPnlData(pnlDataMap);
        
        // Set adjustments data
        const adjustmentsWithSavePending = response.data.adjustments.map((adj) => ({
          ...adj,
          save_pending: false,
        }));
        setAdjustmentsData(adjustmentsWithSavePending);
        
        setDataLoaded(true);
      })
      .catch((error) => {
        notify(
          t("modeling_exchange_rates." + error.response?.data?.error_code || "server_error"),
          "error",
        );
        setDataLoaded(true);
      });
  }, []);
  /* endregion */

  /* region handlers */
  const handleAddAdjustmentButtonClick = () => {
    if (saving) return false;
    
    // Create new empty adjustment and open drawer
    setSelectedAdjustment((draft) => {
      Object.keys(draft).forEach(key => delete draft[key]);
      draft.id = 'new';
      draft.description = '';
      draft.long_description = '';
      draft.priority = 3;
      draft.affected_pnl_lines = [];
      draft.validators = [];
      draft.stage = 1;
      draft.planned_month = null;
      draft.comments = [];
    });
    setNewComment('');
    setPlannedMonthInput('');
    setRejectionComment('');
    setLongDescriptionContent('');
    setDetailsDrawerOpen(true);
  };
  const handleSaveAdjustmentButtonClick = () => {
    if (saving) return false;
    
    // Validation
    if (!selectedAdjustment.description || selectedAdjustment.description.trim().length < 3) {
      notify(t("adjustments.errors.invalid_description"), "error");
      return false;
    }
    
    const params = {
      adjustment_id: selectedAdjustment.id,
      description: selectedAdjustment.description,
      long_description: longDescriptionContent || '',
      priority: selectedAdjustment.priority,
      affected_pnl_lines: selectedAdjustment.affected_pnl_lines || [],
      validators: selectedAdjustment.validators?.map(v => v.id) || [],
      stage: selectedAdjustment.stage || 1,
      planned_month: selectedAdjustment.planned_month || null,
      comment: '', // No comment from drawer anymore
    };
    
    setSaving(true);
    client
      .post("api/admin_adjustments_save/", params)
      .then((response) => {
        // Reload data to get fresh state
        return client.post("api/admin_adjustments/");
      })
      .then((response) => {
        const adjustmentsWithSavePending = response.data.adjustments.map((adj) => ({
          ...adj,
          save_pending: false,
        }));
        setAdjustmentsData(adjustmentsWithSavePending);
        setSaving(false);
        setDetailsDrawerOpen(false);
        notify(t("adjustments.save_success"), "success", 2000);
      })
      .catch((error) => {
        const error_code = error.response?.data?.error_code;
        notify(
          t(error_code ? "adjustments.errors." + error_code : "server_error"),
          "error",
        );
        setSaving(false);
      });
  };
  
  const handleEditAdjustmentClick = (adjustmentId) => {
    const adjustment = adjustmentsData.find(adj => adj.id === adjustmentId);
    if (adjustment) {
      setSelectedAdjustment((draft) => {
        Object.keys(draft).forEach(key => delete draft[key]);
        Object.assign(draft, adjustment);
      });
      setLongDescriptionContent(adjustment.long_description || '');
      setPlannedMonthInput(adjustment.planned_month || '');
      setRejectionComment('');
    }
    setDetailsDrawerOpen(true);
  };
  
  const handleDescriptionChange = (e) => {
    setSelectedAdjustment((draft) => {
      draft.description = e.target.value;
    });
  };
  
  const handlePriorityChange = (e) => {
    setSelectedAdjustment((draft) => {
      draft.priority = e.value;
    });
  };
  
  const handleAffectedLinesChange = (selectedOptions) => {
    setSelectedAdjustment((draft) => {
      draft.affected_pnl_lines = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    });
  };
  
  const handleValidatorsChange = (selectedOptions) => {
    setSelectedAdjustment((draft) => {
      draft.validators = selectedOptions ? selectedOptions.map(opt => ({
        id: opt.value,
        name: opt.label
      })) : [];
    });
  };
  
  const handleDeleteCommentButtonClick = (commentId) => {
    if (saving || deletingComment) return false;
    setCommentIdToDelete(commentId);
    setDeleteCommentDialogOpen(true);
  };
  
  const confirmDeleteComment = () => {
    if (!commentIdToDelete) return;
    
    const params = {
      comment_id: commentIdToDelete,
    };
    
    setDeletingComment(true);
    client
      .post("api/admin_adjustments_delete_comment/", params)
      .then((response) => {
        // Remove comment from selectedAdjustment
        setSelectedAdjustment((draft) => {
          if (draft.comments) {
            draft.comments = draft.comments.filter(c => c.id !== commentIdToDelete);
          }
        });
        
        // Remove comment from adjustmentsData
        setAdjustmentsData((draft) => {
          const index = draft.findIndex(adj => adj.id === selectedAdjustment.id);
          if (index > -1 && draft[index].comments) {
            draft[index].comments = draft[index].comments.filter(c => c.id !== commentIdToDelete);
          }
        });
        
        setDeletingComment(false);
        setDeleteCommentDialogOpen(false);
        setCommentIdToDelete(null);
        notify(t("adjustments.comment_deleted"), "success", 2000);
      })
      .catch((error) => {
        const error_code = error.response?.data?.error_code;
        notify(
          t(error_code ? "adjustments.errors." + error_code : "server_error"),
          "error",
        );
        setDeletingComment(false);
        setDeleteCommentDialogOpen(false);
        setCommentIdToDelete(null);
      });
  };
  
  const handleDeleteAdjustmentButtonClick = (adjustmentId) => {
    if (saving || deletingAdjustment) return false;
    setAdjustmentIdToDelete(adjustmentId);
    setDeleteAdjustmentDialogOpen(true);
  };
  
  const confirmDeleteAdjustment = () => {
    if (!adjustmentIdToDelete) return;
    
    const params = {
      adjustment_id: adjustmentIdToDelete,
    };
    
    setDeletingAdjustment(true);
    client
      .post("api/admin_adjustments_delete/", params)
      .then((response) => {
        // Remove adjustment from adjustmentsData
        setAdjustmentsData((draft) => {
          return draft.filter(adj => adj.id !== adjustmentIdToDelete);
        });
        
        setDeletingAdjustment(false);
        setDeleteAdjustmentDialogOpen(false);
        setAdjustmentIdToDelete(null);
        notify(t("adjustments.delete_success"), "success", 2000);
      })
      .catch((error) => {
        const error_code = error.response?.data?.error_code;
        notify(
          t(error_code ? "adjustments.errors." + error_code : "server_error"),
          "error",
        );
        setDeletingAdjustment(false);
        setDeleteAdjustmentDialogOpen(false);
        setAdjustmentIdToDelete(null);
      });
  };
  
  // Stage Transition Handlers
  const handleStageTransition = (targetStage, additionalComment = '') => {
    if (saving) return false;
    
    // Validation for Stage 2->3: requires planned_month
    if (selectedAdjustment.stage === 2 && targetStage === 3) {
      if (!plannedMonthInput) {
        notify(t("adjustments.errors.planned_month_required"), "error");
        return false;
      }
    }
    
    const params = {
      adjustment_id: selectedAdjustment.id,
      description: selectedAdjustment.description,
      long_description: longDescriptionContent || '',
      priority: selectedAdjustment.priority,
      affected_pnl_lines: selectedAdjustment.affected_pnl_lines || [],
      validators: selectedAdjustment.validators?.map(v => v.id) || [],
      stage: targetStage,
      planned_month: targetStage === 3 ? plannedMonthInput : selectedAdjustment.planned_month,
      comment: additionalComment,
    };
    
    setSaving(true);
    client
      .post("api/admin_adjustments_save/", params)
      .then((response) => {
        // Reload data to get fresh state
        return client.post("api/admin_adjustments/");
      })
      .then((response) => {
        const adjustmentsWithSavePending = response.data.adjustments.map((adj) => ({
          ...adj,
          save_pending: false,
        }));
        setAdjustmentsData(adjustmentsWithSavePending);
        
        // Update selectedAdjustment
        const updatedAdj = adjustmentsWithSavePending.find(adj => adj.id === selectedAdjustment.id);
        if (updatedAdj) {
          setSelectedAdjustment((draft) => {
            Object.keys(draft).forEach(key => delete draft[key]);
            Object.assign(draft, updatedAdj);
          });
          setPlannedMonthInput(updatedAdj.planned_month || '');
        }
        
        setRejectionComment('');
        setSaving(false);
        notify(t("adjustments.stage_transition_success"), "success", 2000);
      })
      .catch((error) => {
        const error_code = error.response?.data?.error_code;
        notify(
          t(error_code ? "adjustments.errors." + error_code : "server_error"),
          "error",
        );
        setSaving(false);
      });
  };
  
  const handleRejectAdjustment = () => {
    if (saving) return false;
    
    // Validation: rejection comment is required
    if (!rejectionComment.trim()) {
      notify(t("adjustments.errors.rejection_comment_required"), "error");
      return false;
    }
    
    // Reject sends back to Stage 3 (Planned) with rejection comment
    handleStageTransition(3, rejectionComment);
  };
  
  const handleCommentsButtonClick = (adjustment) => {
    setCommentAdjustment(adjustment);
    setNewComment('');
    setCommentsDialogOpen(true);
  };
  
  const handleAddComment = () => {
    if (!newComment.trim() || !commentAdjustment) return;
    
    const params = {
      adjustment_id: commentAdjustment.id,
      description: commentAdjustment.description,
      long_description: commentAdjustment.long_description || '',
      priority: commentAdjustment.priority,
      affected_pnl_lines: commentAdjustment.affected_pnl_lines || [],
      validators: commentAdjustment.validators?.map(v => v.id) || [],
      stage: commentAdjustment.stage,
      comment: newComment.trim(),
    };
    
    setSaving(true);
    client
      .post("api/admin_adjustments_save/", params)
      .then((response) => {
        return client.post("api/admin_adjustments/");
      })
      .then((response) => {
        const adjustmentsWithSavePending = response.data.adjustments.map((adj) => ({
          ...adj,
          save_pending: false,
        }));
        setAdjustmentsData(adjustmentsWithSavePending);
        
        // Update commentAdjustment with fresh data
        const updatedAdjustment = adjustmentsWithSavePending.find(adj => adj.id === commentAdjustment.id);
        if (updatedAdjustment) {
          setCommentAdjustment(updatedAdjustment);
        }
        
        setNewComment('');
        setSaving(false);
        notify(t("adjustments.comment_added_success"), "success", 2000);
      })
      .catch((error) => {
        const error_code = error.response?.data?.error_code;
        notify(
          t(error_code ? "adjustments.errors." + error_code : "server_error"),
          "error",
        );
        setSaving(false);
      });
  };
  /* endregion */

  /* region helper functions */
  // Permission functions
  const canDeleteAdjustment = (adjustment) => {
    if (isDev) return true;
    // CAD cannot delete completed adjustments
    if (isCAD && adjustment.stage === 5) return false;
    if (isCAD && adjustment.stage === 1) return true; // Draft
    return false;
  };
  
  const canAdvanceStage = (adjustment) => {
    if (isDev) return true;
    if (isCAD) {
      // CAD can advance from stage 1->2 (Draft->Requested) or 4->5 (Executed->Validated)
      return adjustment.stage === 1 || adjustment.stage === 4;
    }
    return false;
  };
  
  const canEditAdjustment = (adjustment) => {
    if (isDev) return true;
    // CAD cannot edit completed adjustments
    if (isCAD && adjustment.stage === 5) return false;
    return isCAD;
  };
  
  const canCommentAdjustment = (adjustment) => {
    if (isDev) return true;
    // CAD cannot comment on completed adjustments
    if (isCAD && adjustment.stage === 5) return false;
    return true;
  };
  
  const canEditFormFields = () => {
    if (isDev) return true;
    // CAD can only edit form fields in Draft (stage 1)
    if (isCAD && selectedAdjustment?.stage > 1) return false;
    return isCAD;
  };

  function formatAffectedLines(affectedLines) {
    if (!affectedLines || affectedLines.length === 0) return '';
    
    return affectedLines.map(metaName => {
      const line = pnlData[metaName];
      return line ? line.name : metaName;
    }).join(', ');
  }
  
  function parseComment(commentText) {
    if (!commentText || !commentText.includes('|||')) {
      return {
        hasAutomatic: false,
        automaticText: '',
        userComment: commentText || '',
        isRejection: false,
      };
    }
    
    const parts = commentText.split('|||');
    
    // Check for new format: AUTO_CREATED|||stage:N or AUTO_STAGE_CHANGED|||from:N|||to:M or AUTO_REJECTED
    if (parts[0] === 'AUTO_CREATED') {
      // Format: AUTO_CREATED|||stage:N|||user_comment (optional)
      const stageMatch = parts[1]?.match(/stage:(\d+)/);
      if (stageMatch) {
        const stage = stageMatch[1];
        const stageName = t(`adjustments.stages.${stage}`);
        const automaticText = t('adjustments.automatic_messages.created', { stage: stageName });
        return {
          hasAutomatic: true,
          automaticText: automaticText,
          userComment: parts[2] || '',
          isRejection: false,
        };
      }
    } else if (parts[0] === 'AUTO_REJECTED') {
      // Format: AUTO_REJECTED|||from:N|||to:M|||user_comment (optional)
      const fromMatch = parts[1]?.match(/from:(\d+)/);
      const toMatch = parts[2]?.match(/to:(\d+)/);
      if (fromMatch && toMatch) {
        const fromStage = fromMatch[1];
        const toStage = toMatch[1];
        const fromStageName = t(`adjustments.stages.${fromStage}`);
        const toStageName = t(`adjustments.stages.${toStage}`);
        const automaticText = t('adjustments.automatic_messages.rejected', { 
          from: fromStageName, 
          to: toStageName 
        });
        return {
          hasAutomatic: true,
          automaticText: automaticText,
          userComment: parts[3] || '',
          isRejection: true,
        };
      }
    } else if (parts[0] === 'AUTO_STAGE_CHANGED') {
      // Format: AUTO_STAGE_CHANGED|||from:N|||to:M|||user_comment (optional)
      const fromMatch = parts[1]?.match(/from:(\d+)/);
      const toMatch = parts[2]?.match(/to:(\d+)/);
      if (fromMatch && toMatch) {
        const fromStage = fromMatch[1];
        const toStage = toMatch[1];
        const fromStageName = t(`adjustments.stages.${fromStage}`);
        const toStageName = t(`adjustments.stages.${toStage}`);
        const automaticText = t('adjustments.automatic_messages.stage_changed', { 
          from: fromStageName, 
          to: toStageName 
        });
        return {
          hasAutomatic: true,
          automaticText: automaticText,
          userComment: parts[3] || '',
          isRejection: false,
        };
      }
    }
    
    // Fallback for old format or unrecognized format
    return {
      hasAutomatic: true,
      automaticText: parts[0],
      userComment: parts[1] || '',
      isRejection: false,
    };
  }
  
  function renderPriorityChip(priority, isCompleted = false) {
    const priorityMap = {
      1: { label: t("adjustments.priorities.1"), class: classes.priorityChipHigh },
      2: { label: t("adjustments.priorities.2"), class: classes.priorityChipMedium },
      3: { label: t("adjustments.priorities.3"), class: classes.priorityChipLow },
    };
    
    const priorityInfo = priorityMap[priority];
    if (!priorityInfo) return null;
    
    // If completed, use gray style
    const chipClass = isCompleted 
      ? classes.priorityChipCompleted 
      : priorityInfo.class;
    
    return (
      <Chip
        label={priorityInfo.label}
        className={cx(classes.priorityChip, chipClass)}
        size="small"
      />
    );
  }
  
  function getSortedAdjustments() {
    // Separate completed adjustments (stage 5) from not completed
    const completed = adjustmentsData.filter(adj => adj.stage === 5);
    const notCompleted = adjustmentsData.filter(adj => adj.stage !== 5);
    
    // Sort both by priority (1=High, 2=Medium, 3=Low)
    const sortByPriority = (a, b) => a.priority - b.priority;
    
    notCompleted.sort(sortByPriority);
    completed.sort(sortByPriority);
    
    // Return not completed first, then completed
    return [...notCompleted, ...completed];
  }
  
  function formatMonthYear(monthString) {
    if (!monthString) return t("adjustments.not_defined");
    
    // monthString is in format "YYYY-MM"
    const [year, month] = monthString.split('-');
    const date = new Date(year, month - 1);
    
    // Format month according to current language
    const locale = i18n.language === 'pt' ? 'pt-BR' : i18n.language === 'es' ? 'es-ES' : 'en-US';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
  }

  /* endregion */

  /* region components */
  const getStageIcon = (n, adjustment) => {
    return (
      <div
        className={cx({
          [classes.stageIconWrapper]: true,
          ["done"]: adjustment.stage > n,
          ["current"]: adjustment.stage === n,
        })}
        key={n}
      >
      </div>
    );
  };
  
  const renderStageTransitionSection = () => {
    if (!selectedAdjustment || selectedAdjustment.id === 'new') {
      return null; // No stage transition for new adjustments
    }
    
    const currentStage = selectedAdjustment.stage;
    const isIntegration = user?.is_integration;
    
    // Stage 1: Draft - CAD or DEV can send to Requested
    if (currentStage === 1 && (isCAD || isDev)) {
      return (
        <div style={{ 
          marginTop: "24px", 
          padding: "20px", 
          backgroundColor: `${clientColor}08`,
          borderRadius: "6px" 
        }}>
          <h5 style={{ 
            color: grayColor[3], 
            marginBottom: "8px",
            fontSize: "14px",
            fontWeight: "500"
          }}>
            {t("adjustments.stage_transition.title")}
          </h5>
          <p style={{ 
            fontSize: "13px", 
            color: grayColor[6], 
            marginBottom: "16px",
            lineHeight: "1.5",
            whiteSpace: "pre-line"
          }}>
            {t("adjustments.stage_transition.draft_message")}
          </p>
          <Button
            color="client"
            simple
            size="sm"
            onClick={() => handleStageTransition(2)}
            disabled={saving}
          >
            {t("adjustments.stage_transition.send_to_requested")}
          </Button>
        </div>
      );
    }
    
    // Stage 2: Requested - Integration sets planned date and sends to Planned
    if (currentStage === 2) {
      if (isIntegration || isDev) {
        return (
          <div style={{ 
            marginTop: "24px", 
            padding: "20px", 
            backgroundColor: `${clientColor}08`,
            borderRadius: "6px" 
          }}>
            <h5 style={{ 
              color: grayColor[3], 
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500"
            }}>
              {t("adjustments.stage_transition.title")}
            </h5>
            <p style={{ 
              fontSize: "13px", 
              color: grayColor[6], 
              marginBottom: "16px",
              lineHeight: "1.5"
            }}>
              {t("adjustments.stage_transition.requested_message")}
            </p>
            <CustomInput
              id="planned_month"
              labelText={t("adjustments.planned_month")}
              value={plannedMonthInput}
              onChange={(e) => setPlannedMonthInput(e.target.value)}
              formControlProps={{
                style: {
                  width: "100%",
                  marginBottom: "16px",
                },
              }}
              inputProps={{
                type: "month",
                disabled: saving,
              }}
              labelProps={{
                shrink: true,
              }}
              client
            />
            <Button
              color="client"
              simple
              size="sm"
              onClick={() => handleStageTransition(3)}
              disabled={saving || !plannedMonthInput}
            >
              {t("adjustments.stage_transition.send_to_planned")}
            </Button>
          </div>
        );
      } else if (isCAD) {
        return (
          <div style={{ 
            marginTop: "24px", 
            padding: "18px 20px", 
            backgroundColor: `${clientColor}08`,
            borderRadius: "6px" 
          }}>
            <p style={{ 
              fontSize: "13px", 
              color: grayColor[3],
              lineHeight: "1.5",
              margin: 0
            }}>
              {t("adjustments.stage_transition.waiting_integration_plan")}
            </p>
          </div>
        );
      }
    }
    
    // Stage 3: Planned - Integration sends to Executed
    if (currentStage === 3) {
      if (isIntegration || isDev) {
        return (
          <div style={{ 
            marginTop: "24px", 
            padding: "20px", 
            backgroundColor: `${clientColor}08`,
            borderRadius: "6px" 
          }}>
            <h5 style={{ 
              color: grayColor[3], 
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500"
            }}>
              {t("adjustments.stage_transition.title")}
            </h5>
            <p style={{ 
              fontSize: "13px", 
              color: grayColor[6], 
              marginBottom: "16px",
              lineHeight: "1.5"
            }}>
              {t("adjustments.stage_transition.planned_message")}
            </p>
            <Button
              color="client"
              simple
              size="sm"
              onClick={() => handleStageTransition(4)}
              disabled={saving}
            >
              {t("adjustments.stage_transition.send_to_executed")}
            </Button>
          </div>
        );
      } else if (isCAD) {
        const plannedMonthStr = formatMonthYear(selectedAdjustment.planned_month);
        return (
          <div style={{ 
            marginTop: "24px", 
            padding: "18px 20px", 
            backgroundColor: `${clientColor}08`,
            borderRadius: "6px" 
          }}>
            <p style={{ 
              fontSize: "13px", 
              color: grayColor[3],
              lineHeight: "1.5",
              margin: 0
            }}>
              {t("adjustments.stage_transition.executing_message", { month: plannedMonthStr })}
            </p>
          </div>
        );
      }
    }
    
    // Stage 4: Executed - CAD or DEV can Validate or Reject
    if (currentStage === 4 && (isCAD || isDev)) {
      return (
        <div style={{ 
          marginTop: "24px", 
          padding: "20px", 
          backgroundColor: `${clientColor}08`,
          borderRadius: "6px" 
        }}>
          <h5 style={{ 
            color: grayColor[3], 
            marginBottom: "8px",
            fontSize: "14px",
            fontWeight: "500"
          }}>
            {t("adjustments.stage_transition.title")}
          </h5>
          <p style={{ 
            fontSize: "13px", 
            color: grayColor[6], 
            marginBottom: "16px",
            lineHeight: "1.5",
            whiteSpace: "pre-line"
          }}>
            {t("adjustments.stage_transition.executed_message")}
          </p>
          <div style={{ display: "flex", gap: "8px", marginBottom: stageTransitionAction === 'reject' ? "16px" : "0" }}>
            <Button
              color="success"
              simple
              size="sm"
              onClick={() => handleStageTransition(5)}
              disabled={saving}
            >
              {t("adjustments.stage_transition.validate")}
            </Button>
            <Button
              color="danger"
              simple
              size="sm"
              onClick={() => setStageTransitionAction('reject')}
              disabled={saving}
            >
              {t("adjustments.stage_transition.reject")}
            </Button>
          </div>
          
          {stageTransitionAction === 'reject' && (
            <div>
              <CustomInput
                id="rejection_comment"
                labelText={t("adjustments.stage_transition.rejection_comment")}
                value={rejectionComment}
                onChange={(e) => setRejectionComment(e.target.value)}
                formControlProps={{
                  style: {
                    width: "100%",
                    marginBottom: "12px",
                  },
                }}
                inputProps={{
                  multiline: true,
                  rows: 3,
                  disabled: saving,
                  placeholder: t("adjustments.stage_transition.rejection_comment_placeholder"),
                }}
                client
              />
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  color="danger"
                  simple
                  size="sm"
                  onClick={handleRejectAdjustment}
                  disabled={saving || !rejectionComment.trim()}
                  style={{
                    ...(saving || !rejectionComment.trim() ? {
                      backgroundColor: grayColor[13],
                      color: grayColor[7],
                      opacity: 1,
                    } : {})
                  }}
                >
                  {t("adjustments.stage_transition.confirm_rejection")}
                </Button>
                <Button
                  color="transparent"
                  simple
                  size="sm"
                  onClick={() => {
                    setStageTransitionAction(null);
                    setRejectionComment('');
                  }}
                  disabled={saving}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }
    
    // Stage 5: Validated - Only DEV can change with multi-select
    if (currentStage === 5 && isDev) {
      return (
        <div style={{ 
          marginTop: "24px", 
          padding: "20px", 
          backgroundColor: `${clientColor}08`,
          borderRadius: "6px" 
        }}>
          <h5 style={{ 
            color: grayColor[3], 
            marginBottom: "12px",
            fontSize: "14px",
            fontWeight: "500"
          }}>
            {t("adjustments.stage_transition.title")}
          </h5>
          <CustomReactSelect
            title={t("adjustments.stage")}
            isClearable={false}
            closeMenuOnSelect={true}
            options={[
              {value: 1, label: t("adjustments.stages.1")},
              {value: 2, label: t("adjustments.stages.2")},
              {value: 3, label: t("adjustments.stages.3")},
              {value: 4, label: t("adjustments.stages.4")},
              {value: 5, label: t("adjustments.stages.5")},
            ]}
            value={{
              value: selectedAdjustment.stage,
              label: t("adjustments.stages." + selectedAdjustment.stage),
            }}
            onChange={(e) => handleStageTransition(e.value)}
            baseColor={clientColor}
            optionVerticalPadding={"4px"}
            isDisabled={saving}
            style={{
              marginTop: "0px",
            }}
          />
        </div>
      );
    }
    
    // For other cases (non-DEV viewing completed adjustment)
    if (currentStage === 5 && !isDev) {
      return (
        <div style={{ 
          marginTop: "24px", 
          padding: "18px 20px", 
          backgroundColor: `rgba(76, 175, 80, 0.08)`,
          borderRadius: "6px" 
        }}>
          <p style={{ 
            fontSize: "13px", 
            color: grayColor[6],
            lineHeight: "1.5",
            margin: 0
          }}>
            {t("adjustments.stage_transition.completed_message")}
          </p>
        </div>
      );
    }
    
    return null;
  };

  const detailsDrawer = (
    <>
      <Drawer
        anchor={"right"}
        open={detailsDrawerOpen}
        onClose={() => {
          if (saving) return;
          setDetailsDrawerOpen(false);
        }}
        disableEscapeKeyDown={saving}
        BackdropProps={{ style: { backgroundColor: "#000", opacity: "0.1" } }}
        PaperProps={{
          style: {
            borderRadius: "10px",
            margin: "1vh 1vh 1vh 0",
            height: "98vh",
            width: "720px",
            overflowX: "hidden",
          },
        }}
      >
        <div style={{ 
          display: "flex", 
          flexDirection: "column", 
          height: "100%",
          position: "relative"
        }}>
          <ModalHeader
            title={
              selectedAdjustment?.id === "new"
                ? t("adjustments.new_adjustment")
                : t("adjustments.edit_adjustment")
            }
            subtitle={
              selectedAdjustment?.id !== "new"
                ? selectedAdjustment?.description
                : undefined
            }
            pills={[
              selectedAdjustment?.id !== "new" && selectedAdjustment?.stage
                ? {
                    key: "stage",
                    label: t("adjustments.stages." + selectedAdjustment.stage),
                    tone: selectedAdjustment.stage < 5 ? "warning" : "success",
                    dot: true,
                  }
                : null,
            ].filter(Boolean)}
            onClose={() => {
              if (saving) return;
              setDetailsDrawerOpen(false);
            }}
            closeDisabled={saving}
            closeLabel={t("common.close")}
            clientColor={clientColor}
            variant="drawer"
          />
          
          {/* Scrollable Content */}
          <div style={{ 
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "24px 12px 20px 12px"
          }}>
            <GridContainer>
              <GridItem xs={8}>
                <CustomInput
                  id="adjustment_description"
                  labelText={t("adjustments.description")}
                  value={selectedAdjustment?.description || ""}
                  onChange={(e) => handleDescriptionChange(e)}
                  formControlProps={{
                    style: {
                      width: "100%",
                      marginTop: "-20px",
                    },
                  }}
                  inputProps={{
                    type: "text",
                    maxLength: 255,
                    autoComplete: "off",
                    disabled: saving || !canEditFormFields(),
                    style: (saving || !canEditFormFields()) ? { backgroundColor: '#f5f5f5' } : {},
                  }}
                  client
                />
              </GridItem>
              <GridItem xs={4}>
                <CustomReactSelect
                  title={t("adjustments.priority")}
                  isClearable={false}
                  closeMenuOnSelect={true}
                  options={[
                    {value: 1, label: t("adjustments.priorities.1")},
                    {value: 2, label: t("adjustments.priorities.2")},
                    {value: 3, label: t("adjustments.priorities.3")},
                  ]}
                  value={{
                    value: selectedAdjustment?.priority,
                    label: t("adjustments.priorities." + selectedAdjustment?.priority),
                  }}
                  onChange={(e) => handlePriorityChange(e)}
                  baseColor={clientColor}
                  optionVerticalPadding={"4px"}
                  isDisabled={saving || !canEditFormFields()}
                  style={{
                    marginTop: "-10px",
                  }}
                />
              </GridItem>
              <GridItem xs={6}>
                <CustomReactSelect
                  title={t("adjustments.affected_pnl_lines")}
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  options={pnlStructure.map((line) => ({
                    value: line.meta_name,
                    label: line.name,
                  }))}
                  value={
                    selectedAdjustment?.affected_pnl_lines
                      ? selectedAdjustment.affected_pnl_lines.map((metaName) => {
                          const line = pnlData[metaName];
                          return line ? {
                            value: metaName,
                            label: line.name,
                          } : null;
                        }).filter(item => item !== null)
                      : []
                  }
                  onChange={(selectedOptions) => handleAffectedLinesChange(selectedOptions)}
                  baseColor={clientColor}
                  optionVerticalPadding={"4px"}
                  isDisabled={saving || !canEditFormFields()}
                  style={{
                    marginTop: "0px",
                  }}
                />
              </GridItem>
              <GridItem xs={6}>
                <CustomReactSelect
                  title={t("adjustments.validators")}
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  options={usersData.map((user) => ({
                    value: user.id,
                    label: user.name,
                  }))}
                  value={
                    selectedAdjustment?.validators
                      ? selectedAdjustment.validators.map(v => ({
                          value: v.id,
                          label: v.name,
                        }))
                      : []
                  }
                  onChange={(selectedOptions) => handleValidatorsChange(selectedOptions)}
                  baseColor={clientColor}
                  optionVerticalPadding={"4px"}
                  isDisabled={saving || !canEditFormFields()}
                  style={{
                    marginTop: "0px",
                  }}
                />
              </GridItem>
              <GridItem xs={12}>
                <div style={{ marginTop: "20px", marginBottom: "15px" }}>
                  <h6 style={{ color: grayColor[3], marginBottom: "10px" }}>
                    {t("adjustments.detailed_description")}
                  </h6>
                  <TextEditor
                    initialContent={selectedAdjustment?.long_description || ''}
                    setContent={setLongDescriptionContent}
                    readOnly={saving || !canEditFormFields()}
                  />
                </div>
              </GridItem>
              
              {/* Stage transition component */}
              <GridItem xs={12}>
                {renderStageTransitionSection()}
              </GridItem>
            </GridContainer>
          </div>
          
          {/* Fixed Footer */}
          <div
            style={{
              height: "60px",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              padding: "10px 4px",
              backgroundColor: whiteColor,
              borderTop: `1px solid ${grayColor[10]}`,
              flexShrink: 0
            }}
          >
          <Button
            key={"save"}
            aria-label={"Save"}
            color={"client"}
            size={"sm"}
            fullWidth
            onClick={() => handleSaveAdjustmentButtonClick()}
            disabled={saving}
            className={cx({
              [classes.drawerButton]: true,
            })}
          >
            {!saving && t("adjustments.save")}
            {saving && (
              <ThreeDots
                color={clientTextColor}
                ariaLabel="saving"
                width={"20px"}
                height={"12px"}
              />
            )}
          </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
  /* endregion */

  /* region return */
  return (
    <div>
      <GridContainer style={{marginTop: "-40px"}}>
        <GridItem xs={12}>
          <div>
            <Card>
              <CardHeader color="client" icon>
                <CardIcon color="client">
                  <RateReview />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>
                  {t("adjustments.adjustments_list")}
                </h4>
              </CardHeader>
              <CardBody className={classes.cardBody}>
                <GridContainer justifyContent="flex-start">
                  {dataLoaded && Object.keys(usersData).length > 0 && (
                    <>
                      {/* region upper notes */}
                      <GridItem
                        sm={12}
                        className={cx({
                          [classes.comments]: true,
                        })}
                      >
                        {t("adjustments.upper_notes.operation")}
                        {":"}
                        {[...Array(5).keys()].map((key, val) => {
                          return (
                            <React.Fragment key={key}>
                              <br />
                              <span>
                                {"• "}
                                {t("adjustments.upper_notes.note" + (val + 1))}
                              </span>
                            </React.Fragment>
                          );
                        })}
                      </GridItem>
                      {/* endregion */}
                      {/* region adjustments table */}
                      <GridItem xs={12}>
                        <table className={classes.adjustmentsTable}>
                          <thead className={classes.adjustmentsTableHead}>
                          <tr>
                            <td className={classes.adjustmentsTableColDescription}>
                              {t("adjustments.description")}
                            </td>
                            <td className={classes.adjustmentsTableColPriority}>
                              {t("adjustments.priority")}
                            </td>
                            <td className={classes.adjustmentsTableColPnlLines}>
                              {t("adjustments.pnl_line")}
                            </td>
                            <td className={classes.adjustmentsTableColValidators}>
                              {t("adjustments.validator")}
                            </td>
                            <td className={classes.adjustmentsTableColStage}>
                              {t("adjustments.stage")}
                            </td>
                            <td className={classes.adjustmentsTableColPlannedDate}>
                              {t("adjustments.planned_month")}
                            </td>
                            <td className={classes.adjustmentsTableColNextStep}>
                              {t("adjustments.next_step")}
                            </td>
                            <td className={classes.adjustmentsTableColActions}>
                              {t("adjustments.actions")}
                            </td>
                          </tr>
                          </thead>
                          <tbody
                            className={cx({
                              [classes.adjustmentsTableBody]: true,
                              ["saving"]: saving,
                            })}
                          >
                          {getSortedAdjustments().map((adjustment, key) => {
                            const isCompleted = adjustment.stage === 5;
                            return (
                              <tr 
                                className={cx(
                                  classes.adjustmentsTableRow,
                                  isCompleted && classes.adjustmentsTableRowCompleted
                                )} 
                                key={key}
                                onClick={() => {
                                  if (canEditAdjustment(adjustment)) {
                                    handleEditAdjustmentClick(adjustment.id);
                                  }
                                }}
                                style={{ cursor: canEditAdjustment(adjustment) ? "pointer" : "default" }}
                              >
                                <td className={classes.adjustmentsTableColDescription}>
                                  {adjustment.description}
                                </td>
                                <td className={classes.adjustmentsTableColPriority}>
                                  {renderPriorityChip(adjustment.priority, isCompleted)}
                                </td>
                                <td className={classes.adjustmentsTableColPnlLines}>
                                  {formatAffectedLines(adjustment.affected_pnl_lines)}
                                </td>
                                <td className={classes.adjustmentsTableColValidators}>
                                  {adjustment.validators.map((v) => v.name).join(", ")}
                                </td>
                                <td className={classes.adjustmentsTableColStage}>
                                  {[...Array(5).keys()].map((k) => {
                                    return getStageIcon(k + 1, adjustment);
                                  })}
                                  <span style={{marginLeft: "6px"}}></span>
                                  {t("adjustments.stages." + adjustment.stage)}
                                </td>
                                <td className={classes.adjustmentsTableColPlannedDate}>
                                  {adjustment.stage === 3 && adjustment.planned_month
                                    ? formatMonthYear(adjustment.planned_month)
                                    : "N/A"}
                                </td>
                                <td className={classes.adjustmentsTableColNextStep}>
                                  {adjustment.next_step === 'done' 
                                    ? t("adjustments.done") 
                                    : (adjustment.next_step || "-")}
                                </td>
                                  <td className={classes.adjustmentsTableColActions} onClick={(e) => e.stopPropagation()}>
                                    <Tooltip
                                      title={t("adjustments.edit_adjustment")}
                                      arrow
                                      enterDelay={500}
                                    >
                                      <Edit
                                        className={cx({
                                          [classes.iconButtonCustom]: true,
                                          ["disabled"]: !canEditAdjustment(adjustment),
                                        })}
                                        onClick={() => {
                                          if (canEditAdjustment(adjustment)) {
                                            handleEditAdjustmentClick(adjustment.id);
                                          }
                                        }}
                                      />
                                    </Tooltip>
                                    <Tooltip
                                      title={t("adjustments.comments")}
                                      arrow
                                      enterDelay={500}
                                    >
                                      <Chat
                                        className={cx({
                                          [classes.iconButtonCustom]: true,
                                          ["disabled"]: !canCommentAdjustment(adjustment),
                                        })}
                                        onClick={() => {
                                          if (canCommentAdjustment(adjustment)) {
                                            handleCommentsButtonClick(adjustment);
                                          }
                                        }}
                                      />
                                    </Tooltip>
                                    <Tooltip
                                      title={t("adjustments.delete_adjustment")}
                                      arrow
                                      enterDelay={500}
                                    >
                                      <Delete
                                        className={cx({
                                          [classes.iconButtonCustom]: true,
                                          ["disabled"]: !canDeleteAdjustment(adjustment),
                                        })}
                                        onClick={() => {
                                          if (canDeleteAdjustment(adjustment)) {
                                            handleDeleteAdjustmentButtonClick(adjustment.id);
                                          }
                                        }}
                                      />
                                    </Tooltip>
                                  </td>
                              </tr>
                          )
                          })}
                          </tbody>
                        </table>
                      </GridItem>
                      {/* endregion */}
                      {/* region no adjustments listed */}
                      {adjustmentsData.length === 0 && (
                        <SnackbarContent
                          message={t("adjustments.no_adjustments_listed")}
                          color="successLight"
                          paperClass={classes.snackbarPaper}
                          messageClass={classes.snackbarMessage}
                        />
                      )}
                      {/* endregion */}
                      {/* region add adjustment button */}
                      <GridItem
                        sm={12}
                        className={cx({
                          [classes.addNewAdjustmentButton]: true,
                        })}
                      >
                        <Tooltip
                          title={t("adjustments.add_adjustment")}
                          arrow
                          placement="left"
                        >
                          <span>
                            <Button
                              aria-label="add_adjustment"
                              color="client"
                              justIcon
                              size ="sm"
                              onClick={() => handleAddAdjustmentButtonClick()}
                              disabled={saving}
                            >
                              {saving ? (
                                <ThreeDots
                                  width="20px"
                                  height="20px"
                                  color={clientTextColor}
                                  ariaLabel="loading"
                                  wrapperStyle={{}}
                                  wrapperClass=""
                                />
                              ) : (
                                <AddCircle />
                              )}
                            </Button>
                          </span>
                        </Tooltip>
                      </GridItem>
                      {/* endregion */}
                    </>
                  )}
                  {!dataLoaded && loadingSpinner()}
                  {dataLoaded && Object.keys(usersData).length === 0 && (
                    <SnackbarContent
                      message={t("groups.error_on_loading")}
                      color="error"
                      paperClass={classes.snackbarPaper}
                      messageClass={classes.snackbarMessage}
                    />
                  )}
                </GridContainer>
              </CardBody>
            </Card>
          </div>
        </GridItem>
      </GridContainer>
      {detailsDrawer}
      <CommentsDialog
        open={commentsDialogOpen}
        setOpen={setCommentsDialogOpen}
        adjustment={commentAdjustment}
        newComment={newComment}
        setNewComment={setNewComment}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteCommentButtonClick}
        currentUserId={user?.user_id}
        parseComment={parseComment}
        saving={saving}
      />
      <DeleteCommentDialog
        open={deleteCommentDialogOpen}
        setOpen={setDeleteCommentDialogOpen}
        onConfirm={confirmDeleteComment}
        isDeleting={deletingComment}
      />
      <DeleteAdjustmentDialog
        open={deleteAdjustmentDialogOpen}
        setOpen={setDeleteAdjustmentDialogOpen}
        onConfirm={confirmDeleteAdjustment}
        isDeleting={deletingAdjustment}
      />
    </div>
  );
  /* endregion */
}
